
import { useSubscriptions } from "@/hooks/useSubscriptions";
import { SubscriptionCard } from "./SubscriptionCard";
import { useState } from "react";
import { CancellationDialog } from "./CancellationDialog";
import { SubscriptionStats } from "./SubscriptionStats";
import { StatusFilter } from "./StatusFilter";
import { ProductFilter } from "./ProductFilter";
import { SearchBar } from "./SearchBar";
import { LoaderCircle } from "lucide-react";

interface UserSubscriptionsProps {
  schoolId?: string;
  isPublicView?: boolean;
}

export function UserSubscriptions({ schoolId, isPublicView = false }: UserSubscriptionsProps) {
  const { 
    subscriptions, 
    loading, 
    infradigitalConfig, 
    handleCancelSubscription, 
    handleAutoRenewalToggle,
    fetchSubscriptionsForSchool
  } = useSubscriptions(schoolId, isPublicView);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [productFilter, setProductFilter] = useState<string>("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<string | null>(null);

  const handleCancelClick = (id: string) => {
    setSelectedSubscriptionId(id);
    setDialogOpen(true);
  };

  const handleConfirmCancel = () => {
    if (selectedSubscriptionId) {
      handleCancelSubscription(selectedSubscriptionId);
      setDialogOpen(false);
    }
  };

  // Apply filters
  const filteredSubscriptions = subscriptions.filter(subscription => {
    // Status filter
    if (statusFilter !== "all" && subscription.status !== statusFilter) {
      return false;
    }
    
    // Product filter
    if (
      productFilter !== "all" && 
      subscription.plan?.product?.name.toLowerCase() !== productFilter.toLowerCase()
    ) {
      return false;
    }
    
    // Search filter - case insensitive search across product and plan names
    if (searchQuery) {
      const productNameMatch = subscription.plan?.product?.name
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
        
      const planNameMatch = subscription.plan?.name
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
        
      return productNameMatch || planNameMatch;
    }
    
    return true;
  });

  // Get unique product names for the filter
  const productNames = Array.from(
    new Set(
      subscriptions
        .filter(sub => sub.plan?.product?.name)
        .map(sub => sub.plan.product.name)
    )
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <SearchBar 
          value={searchQuery} 
          onChange={setSearchQuery} 
          placeholder="Search subscriptions..." 
        />
        
        <div className="flex gap-2 flex-wrap">
          <StatusFilter 
            value={statusFilter} 
            onChange={setStatusFilter} 
          />
          
          <ProductFilter 
            value={productFilter} 
            onChange={setProductFilter}
            productNames={productNames}
          />
        </div>
      </div>
      
      {loading ? (
        <div className="flex justify-center py-8">
          <LoaderCircle className="h-8 w-8 animate-spin text-gray-500" />
        </div>
      ) : filteredSubscriptions.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <p className="text-gray-500">No subscriptions found.</p>
          <p className="text-sm text-gray-400 mt-2">
            {subscriptions.length > 0 
              ? "Try adjusting your filters to see more results." 
              : "There are no active subscriptions."}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredSubscriptions.map(subscription => (
            <SubscriptionCard
              key={subscription.id}
              subscription={subscription}
              infradigitalConfig={infradigitalConfig}
              onCancelClick={handleCancelClick}
              onAutoRenewalToggle={handleAutoRenewalToggle}
              isPublicView={isPublicView}
            />
          ))}
        </div>
      )}
      
      <CancellationDialog
        isOpen={dialogOpen}
        onOpenChange={setDialogOpen}
        onConfirm={handleConfirmCancel}
      />
    </div>
  );
}
